import { Button, Container, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AccessTime, ChevronRight, Edit as EditIcon } from "@material-ui/icons";
import { Events, Workouts } from "app-interfaces";
import { format } from "date-fns";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { reorderItems } from "../../../config/AppContext";
import { Action, SessionForms } from "../../../types/StaticTypes";
import WorkoutsSuperset from "../../Workouts/SupersetSection";
import SessionClients from "./SessionClients";

const useStyles = makeStyles((theme) => ({
  "session-availability": {
    backgroundColor: "#5A9AE5",
    padding: 15,
    color: "#ffffff",
    "& p": {
      fontSize: 14,
      color: "rgba(255,255,255,0.75)",
    },
    "& svg": {
      height: 14,
      width: 14,
      marginRight: 5,
      marginTop: 3,
      float: "left",
      "&.chevron": {
        float: "right",
        marginRight: 0,
        marginTop: -35,
        height: 23,
        width: 20,
        [theme.breakpoints.up("md")]: {
          marginRight: 15,
          marginTop: -8,
        },
      },
    },
    "& span": {
      fontSize: 14,
      verticalAlign: "text-bottom",
      "&.range": {
        fontWeight: 500,
      },
      "&.format": {
        display: "block",
        marginLeft: 5,
        [theme.breakpoints.up("md")]: {
          display: "inline-block",
          marginLeft: 10,
        },
      },
    },
  },
  "session-details": {
    padding: "0 20px",
    "& h5": {
      marginBottom: 20,
      fontSize: theme.spacing(2),
      fontWeight: 600,
      color: "#525252",
    },
    "& .textEdit": {
      textAlign: "right",
      "& svg": {
        height: 15,
        width: 15,
        fill: "#3E7FE9",
      },
      "& input": {
        color: "#3E7FE9",
        fontSize: 14,
        fontWeight: 500,
        textTransform: "none",
        marginLeft: 5,
        border: "none",
        backgroundColor: "transparent",
      },
    },
    "& textarea": {
      width: "100%",
      padding: 15,
      height: 83,
      fontSize: 14,
      color: "#4A4A4A",
      border: "none",
      backgroundColor: "#F8F8F8",
    },
  },
  "session-menu": {
    padding: 15,
    textTransform: "none",
    "& .MuiGrid-item": {
      border: "6px solid rgba(90, 154, 229, 0.19)",
      borderRadius: "50%",
      height: 70,
      width: 70,
      padding: "10px 0px",
      margin: "auto",
      color: "rgb(62, 127, 233)",
      [theme.breakpoints.up("md")]: {
        height: 90,
        width: 90,
        padding: "15px 0px",
      },
      "&:nth-child(2)": {
        padding: 3,
        color: "rgb(248 248 249)",
        [theme.breakpoints.up("md")]: {
          padding: 3,
        },
        "& div": {
          backgroundColor: "rgb(62, 127, 233)",
          borderRadius: "50%",
          height: 52,
          width: 52,
          padding: "10px 0",
          [theme.breakpoints.up("md")]: {
            height: 72,
            width: 72,
            padding: "15px 0px",
          },
        },
      },
    },
  },
  "session-workout": {
    "& .no-workout": {
      display: "flex",
      margin: "auto",
      padding: "30%",
      textAlign: "center",
    },
    "& .workout-heading": {
      padding: "0 20px",
      "& h5": {
        marginBottom: 20,
        fontSize: theme.spacing(2),
        fontWeight: 600,
        color: "#525252",
        "& button": {
          marginTop: -3,
          textTransform: "none",
          fontSize: 14,
          fontWeight: 500,
          color: "var(--text-primary)",
          "&:hover": {
            backgroundColor: "transparent",
          },
          [theme.breakpoints.up("md")]: {
            marginTop: -4,
          },
        },
      },
      "& .textEdit": {
        marginTop: 2,
        textAlign: "right",
        [theme.breakpoints.up("md")]: {
          marginTop: 0,
        },
        "& svg": {
          height: 15,
          width: 15,
          fill: "#3E7FE9",
        },
        "& input": {
          color: "#3E7FE9",
          fontSize: 14,
          fontWeight: 500,
          height: theme.spacing(3),
          width: "auto",
          padding: 0,
          textTransform: "none",
          marginLeft: 5,
          border: "none",
          backgroundColor: "transparent",
        },
      },
      "& input": {
        width: "100%",
        height: 49,
        padding: 15,
        fontSize: 14,
        color: "#4A4A4A",
        border: "none",
        backgroundColor: "#F8F8F8",
      },
      "& textarea": {
        width: "100%",
        padding: 15,
        height: 83,
        fontSize: 14,
        color: "#4A4A4A",
        border: "none",
        backgroundColor: "#F8F8F8",
      },
    },
    "& .session-workout-sets": {
      marginTop: 50,
      "& button": {
        float: "right",
        color: "#3E7FE9",
        textTransform: "none",
        fontSize: 14,
        fontWeight: 500,
        marginRight: 15,
        marginTop: -6,
        "&:hover": {
          backgroundColor: "transparent",
        },
        "& svg": {
          height: 15,
          width: 15,
          fill: "#3E7FE9",
          marginRight: 5,
        },
      },
      "& #FaB": {
        right: 30,
        bottom: 80,
        [theme.breakpoints.up("md")]: {
          right: 40,
          bottom: 25,
        },
      },
    },
  },
}));

type SessionDetailsProps = {
  // children?: any;
  // eventId: string;
  session: Events.SessionFull;
  time: { start: string; end: string; datetime: Date };
  setCurrentViewFromTargetId: (e: React.MouseEvent<HTMLElement>) => void;
  updateWorkout: (params: { workout: Workouts.IWorkout }) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  completeSession: () => void;
  cancelSession: () => void;
};

// TODO: get rid of all of this direct DOM manipulation. Idiots!
const sessionWorkoutId = React.createRef<HTMLInputElement>();
const sessionWorkoutName = React.createRef<HTMLInputElement>();
const sessionWorkoutDesc = React.createRef<HTMLTextAreaElement>();
const sessionWorkoutNotes = React.createRef<HTMLTextAreaElement>();
const sessionWorkoutDuration = React.createRef<HTMLInputElement>();
const sessionWorkoutExercises = React.createRef<HTMLInputElement>();
const sessionNotes = React.createRef<HTMLTextAreaElement>();

export default function SessionDetails({
  // eventId,
  session,
  time,
  setCurrentViewFromTargetId,
  updateWorkout,
  onSubmit,
}: SessionDetailsProps) {
  const classes = useStyles();

  const workoutReducer = (workout: Workouts.IWorkout, action: Action) => {
    switch (action.type) {
      case "init":
        if (!sessionWorkoutExercises.current) break;
        workout = action.payload;
        sessionWorkoutExercises.current.value = JSON.stringify(workout.exercise_super_set);
        break;
      case "add":
        if (!sessionWorkoutExercises.current) break;
        workout = action.payload;
        sessionWorkoutExercises.current.value = JSON.stringify(workout.exercise_super_set);
        updateWorkout({ workout });
        break;
      case "update":
        if (!sessionWorkoutExercises.current) break;
        workout.exercise_super_set[action.payload.targetIndex].exercises = JSON.parse(
          action.payload.newSuperSet
        );
        sessionWorkoutExercises.current.value = JSON.stringify(workout.exercise_super_set);
        updateWorkout({ workout });
        break;
      case "update-exercise":
        if (!sessionWorkoutExercises.current) break;
        workout.exercise_super_set[action.payload.supersetIndex].exercises[
          action.payload.exerciseIndex
        ] = action.payload.exercise;
        sessionWorkoutExercises.current.value = JSON.stringify(workout.exercise_super_set);
        updateWorkout({ workout });
        break;
      case "reorder":
        const { supersetId, targetId } = action.payload as {
          supersetId: number;
          targetId: number;
        };

        workout.exercise_super_set = reorderItems(supersetId, targetId, workout.exercise_super_set);

        updateWorkout({ workout });
        break;
      default:
        throw new Error(`${action.type} is not a valid option`);
    }
    return workout;
  };

  const initialWorkout = session.workout || {
    id: "",
    name: "",
    description: "",
    duration: 0,
    is_deleted: false,
    exercise_super_set: [],
  };
  const [workout, workoutDispatch] = React.useReducer(workoutReducer, initialWorkout);

  const editSessionWorkout = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    e.preventDefault();
    const currentTarget = e.currentTarget;
    const state = currentTarget.getAttribute("data-type");
    const ff = document.getElementById("submit");
    if (
      sessionWorkoutName.current &&
      sessionWorkoutDesc.current &&
      sessionWorkoutNotes.current &&
      sessionWorkoutDuration.current &&
      state === "edit" &&
      ff
    ) {
      sessionWorkoutName.current.removeAttribute("disabled");
      sessionWorkoutName.current.setAttribute("required", "true");
      sessionWorkoutName.current.classList.toggle("form-control");
      sessionWorkoutDuration.current.removeAttribute("disabled");
      sessionWorkoutDuration.current.setAttribute("required", "true");
      sessionWorkoutDuration.current.classList.toggle("form-control");
      sessionWorkoutDesc.current.removeAttribute("disabled");
      sessionWorkoutDesc.current.setAttribute("required", "true");
      sessionWorkoutDesc.current.classList.toggle("form-control");
      sessionWorkoutNotes.current.removeAttribute("disabled");
      sessionWorkoutNotes.current.classList.toggle("form-control");
      currentTarget.setAttribute("hidden", "true");
      ff.removeAttribute("hidden");
    }
  };

  const SessionAvailibility: React.FC = () => {
    const { t } = useTranslation();
    if (!time) return <></>;
    return (
      <Container
        id="reschedule"
        className={classes["session-availability"]}
        onClick={setCurrentViewFromTargetId}
      >
        <Typography variant="body1">{t("Time")}</Typography>
        <AccessTime />
        <Typography variant="body2" component="span" className="range">
          {time.start} - {time.end}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className="format"
          style={{ textTransform: "capitalize" }}
        >
          {` ${t(`${format(new Date(time.datetime), "EEEE")}`)} ${t(
            `${format(new Date(time.datetime), "MMM").toLowerCase()}`
          )} ${format(new Date(time.datetime), "dd")}`}
        </Typography>
        <ChevronRight className="chevron" />
      </Container>
    );
  };
  const findPercentage = () => {
    var totalCnt = workout?.exercise_super_set?.length || 0,
      total_perc;
    var completeSets = 0;
    //check if any of them have empty results or 0.0
    workout?.exercise_super_set?.forEach((sets) => {
      var hasResultEmpty = true;
      sets.exercises?.forEach((ex) => {
        // console.log('ex.result_vals', ex.result_vals)
        hasResultEmpty = (!ex.result_vals?.length && true) || ex.result_vals.includes(0.0);
      });
      if (!hasResultEmpty) {
        completeSets += 1;
      }
    });
    if (totalCnt === 0) {
      total_perc = 0;
    } else {
      total_perc = parseInt(`${(parseFloat(`${completeSets}`) / totalCnt) * 100}`);
    }
    // console.log('total_perc',completeSets,totalCnt, total_perc)
    return total_perc;
  };
  const SessionNotes: React.FC = () => {
    const { t } = useTranslation();
    const [isInEditMode, setIsInEditMode] = useState(false);
    const handleEditButtonClick = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
      e.preventDefault();
      setIsInEditMode(true);
    };

    const editButton = (
      <input type="button" defaultValue={`${t("Edit")}`} onClick={handleEditButtonClick} />
    );

    const saveButton = <input id="submitSession" type="submit" value={`${t("Save")}`} />;

    return (
      <form
        id={SessionForms[SessionForms.SESSIONDETAILS]}
        onSubmit={onSubmit}
        className={classes["session-details"]}
      >
        <Grid container>
          <Grid item xs={10}>
            <Typography variant="h5">{t("Session notes")}</Typography>
          </Grid>
          <Grid item xs={2} className="textEdit">
            <span>
              <EditIcon />
              {isInEditMode ? saveButton : editButton}
            </span>
          </Grid>
        </Grid>
        <textarea
          disabled={!isInEditMode}
          defaultValue={session.notes}
          id="sessionNotes"
          name="sessionNotes"
          ref={sessionNotes}
        />
        <input
          name="sessionWorkoutId"
          type="text"
          style={{ display: "none" }}
          defaultValue={workout ? workout.id : ""}
          ref={sessionWorkoutId}
        />
      </form>
    );
  };

  const SessionMenu: React.FC = () => {
    const { t } = useTranslation();
    if (!workout) return <></>;
    const exercises = workout.exercise_super_set.length;
    return (
      <form id="sessionMenu" className={classes["session-menu"]}>
        <Grid container style={{ textAlign: "center" }}>
          <Grid item>
            <span style={{ fontSize: 16, fontWeight: "bold" }}>{exercises}</span>
            <p style={{ fontSize: window.innerWidth > 700 ? 14 : 10 }}>
              {exercises > 1 ? t("Exercises") : t("Exercise")}
            </p>
          </Grid>
          <Grid item>
            <div>
              <span style={{ fontSize: 16, fontWeight: "bold" }}>
                {workout && workout.duration}
              </span>
              <p style={{ fontSize: window.innerWidth > 700 ? 14 : 10 }}>{t("Mins")}</p>
            </div>
          </Grid>
          <Grid item>
            {/* {console.log('exercises', exercises, workout)} */}

            <span style={{ fontSize: 16, fontWeight: "bold" }}>
              {workout.exercise_super_set ? findPercentage() : "0"}%
            </span>
            <p style={{ fontSize: window.innerWidth > 700 ? 14 : 10 }}>{t("Done")}</p>
          </Grid>
        </Grid>
      </form>
    );
  };

  const SessionWorkouts: React.FC = () => {
    const { t } = useTranslation();
    if (!workout)
      return (
        <div className={classes["session-workout"]}>
          <div className="no-workout">{t("No workouts added")}</div>
        </div>
      );
    return (
      <div className={classes["session-workout"]}>
        <form id={SessionForms[SessionForms.WORKOUTS]} onSubmit={onSubmit}>
          <div className="workout-heading">
            <Grid container>
              <Grid item xs={10}>
                <Typography variant="h5">
                  {t("Workouts")}
                  <Button id="template" variant="text" onClick={setCurrentViewFromTargetId}>
                    {t("Change workout")}
                  </Button>
                </Typography>
              </Grid>
              <Grid item xs={2} className="textEdit">
                <EditIcon />
                <input
                  type="button"
                  defaultValue={`${t("Edit")}`}
                  onClick={editSessionWorkout}
                  data-type="edit"
                />
                <input
                  id="submit"
                  hidden={true}
                  type="submit"
                  value={`${t("Save")}`}
                  data-type="save"
                />
              </Grid>
            </Grid>
            <input
              type="text"
              defaultValue={workout.id}
              id="sessionWorkoutId"
              name="sessionWorkoutId"
              ref={sessionWorkoutId}
              style={{ display: "none" }}
            />
            <input
              type="text"
              defaultValue={workout.name}
              id="sessionWorkoutName"
              name="sessionWorkoutName"
              disabled={true}
              ref={sessionWorkoutName}
            />
          </div>
          <br />
          <div className="workout-heading">
            <Typography variant="h5">
              {t("Duration")} ({t("Minutes")})
            </Typography>
            <input
              type="text"
              defaultValue={workout.duration}
              id="sessionWorkoutDuration"
              name="sessionWorkoutDuration"
              disabled={true}
              ref={sessionWorkoutDuration}
            />
          </div>
          <br />
          <div className="workout-heading">
            <Typography variant="h5">{t("Description")}</Typography>
            <textarea
              defaultValue={workout.description}
              id="sessionWorkoutDesc"
              name="sessionWorkoutDesc"
              disabled={true}
              ref={sessionWorkoutDesc}
            />
          </div>
          <br />
          <div className="workout-heading">
            <Typography variant="h5">{t("Notes")}</Typography>
            <textarea
              id="sessionWorkoutNotes"
              name="sessionWorkoutNotes"
              defaultValue={workout.notes}
              disabled={true}
              ref={sessionWorkoutNotes}
            />
          </div>
          <div id="session-workout" className="session-workout-sets">
            <input
              type="text"
              defaultValue={JSON.stringify(workout.exercise_super_set)}
              id="sessionWorkoutExercises"
              name="sessionWorkoutExercises"
              ref={sessionWorkoutExercises}
              style={{ display: "none" }}
            />
            {workout.exercise_super_set.length > 0 && (
              <Button id="edit-workout" variant="text" onClick={setCurrentViewFromTargetId}>
                <EditIcon />
                {t("Edit")}
              </Button>
            )}
            <WorkoutsSuperset workout={workout} session={session} dispatch={workoutDispatch} />
          </div>
        </form>
      </div>
    );
  };

  // React.useEffect(() => {
  //   if (!workout && session.workout) {
  //     const workout = session.workout;
  //     workoutDispatch({ type: "init", payload: workout });
  //   }
  // }, [eventId, session, workout, workoutDispatch]);

  const { t } = useTranslation();

  return (
    <div id="Session" className="session">
      <SessionClients session={session} />
      <SessionAvailibility />
      <br />
      {workout ? (
        <>
          <SessionNotes />
          <br />
          <SessionMenu />
          <br />
          <SessionWorkouts />
        </>
      ) : (
        <div className={classes["session-workout"]}>
          <div className="no-workout">{t("Add workout")}</div>
        </div>
      )}
    </div>
  );
}
